import { MeasureData } from "../interfaces";
import { BODY_FAT_RANGE_PERCENT } from "./calculate-muscle";
import { formatChange, metricChange } from "../../design-system/lib/metric-format";

export interface MeasurementDelta {
    /** Latest reading minus first, between the figures as displayed. Negative is a reduction. */
    value: number;
    /** Signed with U+2212 MINUS and the change's unit, e.g. "−5 cm", "−3.0 pts". */
    text: string;
    /**
     * Whether the change reads as progress. Height changes between scans are
     * measurement noise; presenting one like a waist change tells the client
     * they got shorter.
     */
    meaningful: boolean;
}

/** Keys whose change carries no progress reading. */
const NOT_PROGRESS = new Set(['height', 'head']);

export interface MetricReading {
    scanID: string;
    date: Date;
    value: number;
}

export interface MetricPair {
    /** The first scan with a reading of this metric. */
    start: MetricReading;
    /** The latest scan with a reading of this metric. */
    now: MetricReading;
    /** now − start, between the figures as displayed; see metric-format.ts. */
    change: number;
}

/**
 * Whether a stored value is a reading. The scan pipeline writes 0 for anything
 * it could not measure, and body fat is NaN when the tape cannot produce one,
 * so neither is a reading. Body fat outside the plausible range is dropped by
 * the same rule bodyFatReadings uses, so every surface pairs the same scans.
 */
export const isReading = (key: string, v: unknown): v is number =>
    typeof v === 'number' &&
    Number.isFinite(v) &&
    v !== 0 &&
    (key !== 'body_fat' || (v >= BODY_FAT_RANGE_PERCENT.min && v <= BODY_FAT_RANGE_PERCENT.max));

/** Every reading of one metric, oldest first. */
export const metricReadings = (
    measurements: MeasureData[],
    key: keyof MeasureData["measurements"]
): MetricReading[] =>
    measurements
        .map((m) => ({
            scanID: m.ID,
            date: new Date(m.createdAt),
            value: m.measurements?.[key] as unknown,
        }))
        .filter((r): r is MetricReading => isReading(key as string, r.value))
        .sort((a, b) => a.date.getTime() - b.date.getTime());

/**
 * The one scan pair every change on the dashboard is taken over: the first
 * scan with a reading of this metric, and the latest. Null with fewer than two.
 *
 * The hero, the change strip, the Avatar tape list and the Measurements cards
 * each worked this out themselves, and each rounded differently, so one waist
 * read −5 in the hero and −4 a tab away.
 */
export const metricPair = (
    measurements: MeasureData[],
    key: keyof MeasureData["measurements"]
): MetricPair | null => {
    const readings = metricReadings(measurements, key);
    if (readings.length < 2) return null;
    const start = readings[0];
    const now = readings[readings.length - 1];
    return { start, now, change: metricChange(key as string, start.value, now.value) };
};

export const buildMeasurementDeltas = (
    measurements: MeasureData[],
    keys: (keyof MeasureData["measurements"])[]
): Map<string, MeasurementDelta> => {
    const out = new Map<string, MeasurementDelta>();
    for (const key of keys) {
        const pair = metricPair(measurements, key);
        if (!pair) continue;
        out.set(key as string, {
            value: pair.change,
            text: formatChange(key as string, pair.change),
            meaningful: !NOT_PROGRESS.has(key as string),
        });
    }
    return out;
};
